/* eslint-disable max-lines-per-function */
import {
  Antrag,
  Arbeitsbereich,
  Mitarbeiter,
} from '@/domain/arbeitszeiten/entities';
import { GET_ANTRAG_BY_ID } from '@/domain/arbeitszeiten/hooks/graphql/queries';
import {
  Arbeitszeit_GetAntragByIdQuery,
  Arbeitszeit_GetAntragByIdQueryVariables,
  Markierung,
} from '@/gql/graphql';
import { getDateOrNull } from '@/utils/helperFunctions';
import { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

export function useAntrag({
  antragId,
  standortId,
  datum,
}: {
  antragId: string;
  standortId: string;
  datum: string;
}): { data: Antrag | null; error: ErrorLike | null; loading: boolean } {
  const { data, error, loading } = useQuery<
    Arbeitszeit_GetAntragByIdQuery,
    Arbeitszeit_GetAntragByIdQueryVariables
  >(GET_ANTRAG_BY_ID, {
    variables: {
      antragid: antragId,
      standortId: standortId,
      datum: datum,
    },
    skip: !antragId,
  });

  if (loading) return { data: null, error: null, loading };
  if (error) return { data: null, error, loading };

  const a = data?.arbeitszeiten_antrag;
  if (!a) return { data: null, error: null, loading };

  const antrag = new Antrag(
    a.id ?? '',
    a.status ?? undefined,
    a.arbeitszeitId ?? '',
    a.offen ?? false,
    a.bemerkungAntragsteller ?? '',
    a.bemerkungVorgesetzter ?? '',
    getDateOrNull(a.datum) ?? undefined,
    getDateOrNull(a.aenderungsdatum) ?? undefined,
    getDateOrNull(a.erstellungsdatum) ?? undefined,
    new Arbeitsbereich(
      a.arbeitsbereich?.name ?? '',
      a.arbeitsbereich?.id ?? '',
      a.arbeitsbereich?.markierung as unknown as Markierung,
      [],
    ),
    new Mitarbeiter(
      a.mitarbeiter?.vorname ?? '',
      a.mitarbeiter?.nachname ?? '',
      a.mitarbeiter?.id ?? '',
      false,
      a.mitarbeiter?.bildUrl ?? undefined,
    ),
    {
      time: getDateOrNull(a.beginn) ?? undefined,
      isChangeRequested: a.arbeitszeitId === null,
    },
    {
      time: getDateOrNull(a.ende) ?? undefined,
      isChangeRequested: a.arbeitszeitId === null,
    },
    {
      time: getDateOrNull(a.beginn2.time) ?? undefined,
      isChangeRequested: a.beginn2.isChangeRequested,
    },
    {
      time: getDateOrNull(a.ende2.time) ?? undefined,
      isChangeRequested: a.ende2.isChangeRequested,
    },
  );

  const historie = a.antragsistorie.map(
    (resultAntrag) =>
      new Antrag(
        resultAntrag.id ?? '',
        resultAntrag.status ?? undefined,
        resultAntrag.arbeitszeitId ?? '',
        resultAntrag.offen ?? false,
        resultAntrag.bemerkungAntragsteller ?? '',
        resultAntrag.bemerkungVorgesetzter ?? '',
        getDateOrNull(resultAntrag.datum) ?? undefined,
        getDateOrNull(resultAntrag.aenderungsdatum) ?? undefined,
        getDateOrNull(a.erstellungsdatum) ?? undefined,
        new Arbeitsbereich(
          resultAntrag.arbeitsbereich?.name ?? '',
          resultAntrag.arbeitsbereich?.id ?? '',
          resultAntrag.arbeitsbereich?.markierung as unknown as Markierung,
          [],
        ),
        new Mitarbeiter(
          resultAntrag.mitarbeiter?.vorname ?? '',
          resultAntrag.mitarbeiter?.nachname ?? '',
          resultAntrag.mitarbeiter?.id ?? '',
          false,
          resultAntrag.mitarbeiter?.bildUrl ?? undefined,
        ),
        {
          time: getDateOrNull(resultAntrag.beginn) ?? undefined,
          isChangeRequested: resultAntrag.arbeitszeitId === null,
        },
        {
          time: getDateOrNull(resultAntrag.ende) ?? undefined,
          isChangeRequested: resultAntrag.arbeitszeitId === null,
        },
        {
          time: getDateOrNull(resultAntrag.beginn2.time) ?? undefined,
          isChangeRequested: resultAntrag.beginn2.isChangeRequested,
        },
        {
          time: getDateOrNull(resultAntrag.ende2.time) ?? undefined,
          isChangeRequested: resultAntrag.ende2.isChangeRequested,
        },
      ),
  );
  antrag.historie = historie;
  return { data: antrag, error: null, loading };
}
